import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import Icon from "../Icon/Icon";
import "./Checkbox.css";

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** text next to the box; anything (an avatar + name works too) */
  label?: ReactNode;
  /** grey text under the label */
  description?: ReactNode;
  /** "some selected" dash, for a select-all box */
  indeterminate?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** for screen readers when there is no visible label */
  ariaLabel?: string;
}

/**
 * Tick box with an optional label.
 * Example: <Checkbox checked={on} onChange={setOn} label="Select all" />
 */
const Checkbox = ({
  checked,
  onChange,
  label,
  description,
  indeterminate = false,
  disabled = false,
  id,
  className = "",
  ariaLabel,
}: CheckboxProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      className={`ds-checkbox ${disabled ? "is-disabled" : ""} ${className}`.trim()}
    >
      <span className="ds-checkbox__box">
        <input
          ref={inputRef}
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-label={ariaLabel}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="ds-checkbox__mark" aria-hidden="true">
          <Icon
            name={indeterminate && !checked ? "minus" : "check"}
            size={14}
          />
        </span>
      </span>
      {(label || description) && (
        <span className="ds-checkbox__text">
          {label && <span className="ds-checkbox__label">{label}</span>}
          {description && (
            <span className="ds-checkbox__description">{description}</span>
          )}
        </span>
      )}
    </label>
  );
};

export default Checkbox;
